import { computed, type MaybeRefOrGetter, type Ref } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'

import { MemberStatus } from '@communities/common-layer/types/membership.types'

import { useCommunityUserQueryStore } from '../stores/community-user.query-store'
import { useCommunityUserUIStore } from '../stores/community-user.ui-store'

/**
 * Community User Composable - Orchestrates community user data fetching and UI state
 * Provides a clean API for reading community user data and managing user profiles
 *
 * @example
 * // Use the composable
 * const communityUser = useCommunityUser()
 *
 * // Create queries with specific parameters
 * const userDetails = communityUser.fetchUserDetailsForGroup(groupId, locationId)
 * const userProfile = communityUser.fetchUserDetailsBySlug(userSlug)
 * const userGroups = communityUser.fetchUserGroups({ contactId, limit: 30 })
 */
export const useCommunityUser = () => {
  // Initialize stores
  const queryStore = useCommunityUserQueryStore()
  const uiStore = useCommunityUserUIStore()
  const { store: authStore } = useAuth()

  // ===== BUSINESS LOGIC METHODS =====
  const fetchUserProfile = async (slug: string) => {
    try {
      // Create a query for this specific slug
      const userQuery = queryStore.fetchUserDetailsBySlugQuery(slug)
      await userQuery.suspense()
      const response = userQuery.data.value

      if (!response) {
        throw new Error(`No user found with slug: ${slug}`)
      }

      // Update UI store with the fetched data
      uiStore.setInitialized(true)

      return response
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      throw error
    }
  }

  const reset = () => {
    uiStore.reset()
  }

  // ===== PAYMENT MUTATION FUNCTIONS =====
  const initiateGroupPayment = async (params: {
    groupId: string
    contactId: string
    orderId: string
    orderSourceId: string
    locationId: string
  }) => {
    return queryStore.initiateGroupPaymentMutation.mutateAsync(params)
  }

  const captureGroupPayment = async (params: {
    groupId: string
    contactId: string
    orderId: string
    orderSourceId: string
    locationId: string
    /** Route only — body stays `{ orderId, orderSourceId }` for v1 and v2. */
    useV2?: boolean
  }) => {
    return queryStore.captureGroupPaymentMutation.mutateAsync(params)
  }

  const joinGroup = async (params: {
    locationId: string
    groupId: string
    role: string
    orderId?: string
    contactId?: string
    status?: string
  }) => {
    return queryStore.joinGroupMutation.mutateAsync(params)
  }

  /**
   * Reactive flag indicating whether the current user can interact with the
   * given group (post, comment, react, reply, etc.).
   *
   * Resolves to `true` only when:
   * - The user is authenticated, AND
   * - The user has an ACTIVE membership row for this group.
   *
   * Use this as the single source of truth across community components to gate
   * UI affordances (hide composer, disable like button) or block actions before
   * showing the Join Group modal. Backed by the same TanStack query as every
   * other consumer of `fetchCurrentUserDetailsForGroup`, so the cache is shared.
   *
   * @example
   * const { canInteractInGroup } = useCommunityUser()
   * const canInteract = canInteractInGroup(props.groupId, props.locationId)
   * // <CommentEditor v-if="canInteract" />
   */
  const canInteractInGroup = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>
  ): Ref<boolean> => {
    const userGroupDetails = queryStore.fetchCurrentUserDetailsForGroupQuery(groupId, locationId, () => authStore.isLoggedIn)
    return computed(() => userGroupDetails.data.value?.status === MemberStatus.ACTIVE)
  }

  return {
    // Mutation interfaces
    initiateGroupPayment,
    captureGroupPayment,
    joinGroup,

    // Business logic methods
    fetchUserProfile,
    reset,

    // UI store
    ui: uiStore,

    // Query creation functions
    fetchGokollabContactIdQuery: queryStore.fetchGokollabContactIdQuery,
    fetchUserDetailsBySlug: queryStore.fetchUserDetailsBySlugQuery,
    fetchCurrentUserDetailsForGroup: (
      groupId: MaybeRefOrGetter<string | undefined>,
      locationId: MaybeRefOrGetter<string | undefined>,
      isAuthenticated: MaybeRefOrGetter<boolean> = false
    ) => queryStore.fetchCurrentUserDetailsForGroupQuery(groupId, locationId, isAuthenticated),
    fetchUserDetailsForGroupByContact: (
      contactId: MaybeRefOrGetter<string | undefined>,
      groupId: MaybeRefOrGetter<string | undefined>,
      locationId: MaybeRefOrGetter<string | undefined>
    ) => queryStore.fetchUserDetailsForGroupByContactQuery(contactId, groupId, locationId),
    fetchUserGroups: queryStore.fetchUserGroupsInfiniteQuery,
    fetchUserContributions: queryStore.fetchUserContributionsInfiniteQuery,
    fetchUserActivityStats: queryStore.fetchUserActivityStatsQuery,

    // Membership gate — reactive `Ref<boolean>` shared across all community components
    canInteractInGroup,
  }
}
